import {
  daysRange,
  formatDate,
  formatDateRange,
  formatDateTime,
  toUtcDate,
  toUtcDateTime,
} from '@src/utils';
import dayjs from 'dayjs';

import { ABS_DEFAULT_END_TIME, ABS_DEFAULT_START_TIME } from '../constants';
import { getSlotFreeDates } from './calendar';
import { getUserWorktimeForDate } from './resources';

function isNextDay(a, b) {
  const aNext = a._date.add(1, 'day');
  return aNext.isSame(b._date, 'day');
}

/**
 * Collects absence details for a given absence ID.
 *
 * Retrieves key attributes from the targeted absence record and, if available,
 * from its associated neighbors (whole activity).
 *
 * @param {number|string} absenceId
 *        Unique identifier of the absence to collect.
 * @param {Array<object>} absences
 *        List of absence objects to search through.
 */
export function getAbsenceDataById(absenceId, absences) {
  if (!absenceId || !Array.isArray(absences) || absences.length === 0) {
    return null;
  }

  const absNumberId = Number(absenceId);
  const anchor = absences.find((a) => a.id === absNumberId);
  if (!anchor) {
    return null;
  }

  if (!anchor.all_day) {
    return {
      user_id: anchor.acti_user,
      date_from: anchor.acti_date,
      date_to: anchor.acti_date,
      acti_type: anchor.acti_type,
      acti_tentative: anchor.acti_tentative,
      acab_notes: anchor.acab_notes,
    };
  }

  // Normalize with parsed date
  const normalized = absences.map((a) => {
    const _date = dayjs(a.acti_date);
    return { ...a, _date };
  });

  const sameGroup = (x) => {
    return (
      String(x.acti_user) === String(anchor.acti_user) &&
      x.all_day &&
      x.acti_type === anchor.acti_type
    );
  };

  // Collect all candidate items for this user/type
  const candidates = normalized
    .filter(sameGroup)
    .sort((a, b) => a._date.get('date') - b._date.get('date'));

  const anchorIdx = candidates.findIndex((a) => a.id === absNumberId);
  if (anchorIdx === -1) {
    return null;
  }

  // Expand left while consecutive by date (-1 day steps)
  let left = anchorIdx;
  while (left - 1 >= 0 && isNextDay(candidates[left - 1], candidates[left])) {
    left -= 1;
  }

  // Expand right while consecutive by date (+1 day steps)
  let right = anchorIdx;
  while (
    right + 1 < candidates.length &&
    isNextDay(candidates[right], candidates[right + 1])
  ) {
    right += 1;
  }

  const block = candidates.slice(left, right + 1);
  return {
    user_id: block[0].acti_user,
    date_from: block[0].acti_date,
    date_to: block[block.length - 1].acti_date,
    acti_type: block[0].acti_type,
    acti_tentative: block[0].acti_tentative,
    acab_notes: block[0].acab_notes,
  };
}

function getSelectedAbsenceDuration(absence, slot) {
  if (
    !slot ||
    slot.absences_ids?.length !== 1 ||
    slot.absences_ids[0] !== absence.id
  ) {
    return null;
  }
  if (!slot.startTime && !slot.endTime) {
    return null;
  }

  return {
    allDay: false,
    ...(slot.startTime && {
      start: formatDateTime(slot.date_from, slot.startTime),
    }),
    ...(slot.endTime && { end: formatDateTime(slot.date_from, slot.endTime) }),
  };
}
export function absencesToCalendarEvents(slot, data, dayView = false) {
  if (!data?.length) {
    return [];
  }

  function absenceDuration(item) {
    let duration = {
      start: item.acti_date,
      end: item.acti_date,
      allDay: item.all_day,
    };
    if (item.acti_start_time) {
      duration = {
        ...duration,
        start: formatDateTime(item.acti_date, item.acti_start_time),
      };
    }
    if (item.acti_end_time) {
      duration = {
        ...duration,
        end: formatDateTime(item.acti_date, item.acti_end_time),
      };
    }
    const slotDuration = getSelectedAbsenceDuration(item, slot);
    if (slotDuration) {
      duration = {
        ...duration,
        ...slotDuration,
      };
    }
    return duration;
  }
  return data.map((item) => {
    const eventData = getAbsenceDataById(item.id, data);
    return {
      id: item.id,
      resourceId: `user-${item.acti_user}`,
      type: item.acti_type,
      color: item.acti_colour,
      block_position: item.block_position,
      workday_count: item.workday_count,
      show_workday_count: item.show_workday_count,
      editalbe: false,
      ...absenceDuration(item),
      ...(dayView && {
        resourceEditable: false,
        startEditable: false,
        durationEditable: true,
        extendedProps: {
          label: getEventLabel(eventData.date_from, eventData.date_to, true),
        },
      }),
    };
  });
}

function getEventLabel(start, end, dayView = false) {
  if (!dayView) return dayjs(start).get('date');
  if (dayjs(start).isSame(dayjs(end))) {
    return formatDate(start, {
      day: '2-digit',
      month: '2-digit',
    });
  } else {
    return formatDateRange(start, end, {
      day: '2-digit',
      month: '2-digit',
    });
  }
}
export function getTransientEventsForSlot(
  slot,
  absences,
  users,
  dayView = false,
) {
  if (!slot) {
    return [];
  }
  const userAbsences = getUserAbsences(slot.user_id, absences);
  const slotFreeDates = getSlotFreeDates(slot, userAbsences);
  if (!slotFreeDates.length) {
    return [];
  }

  if (dayView) {
    const userWorktime = getUserWorktimeForDate(
      slot.user_id,
      users,
      slot.date_from,
    );
    const startTime = slot.startTime || userWorktime?.start;
    const endTime = slot.endTime || userWorktime?.end;
    const label = getEventLabel(slot.date_from, slot.date_from, true);
    return [
      {
        id: `transient-event-${slot.start}`,
        resourceId: slot.resource_id,
        start: formatDateTime(slot.date_from, startTime),
        end: formatDateTime(slot.date_to, endTime),
        classNames: ['transient-event'],
        extendedProps: { label: label },
        startEditable: false,
        durationEditable: true,
        resourceEditable: false,
      },
    ];
  }
  const user = users.find((u) => u.id === slot.user_id);
  const worktime = user.worktime;

  const transientEvents = slotFreeDates.map((day) => {
    const label = getEventLabel(day, day, dayView);
    const event = {
      id: `transient-event-${toUtcDate(day)}`,
      resourceId: slot.resource_id,
      start: dayView
        ? toUtcDateTime(day.hour(ABS_DEFAULT_START_TIME))
        : toUtcDate(day),
      end: dayView
        ? toUtcDateTime(day.hour(ABS_DEFAULT_END_TIME))
        : toUtcDate(day),
      classNames: ['transient-event'],
      extendedProps: { label: label },
      editable: false,
    };

    const dayNumber = day.day();
    const wt = worktime.find((item) => item.dewt_weekday + 1 === dayNumber); // FIXME
    if (wt) {
      const [sh, sm] = wt.dewt_start_time_morning.split(':');
      const [eh, em] = wt.dewt_end_time_afternoon.split(':');
      event.start = toUtcDateTime(day.hour(sh).minute(sm));
      event.end = toUtcDateTime(day.hour(eh).minute(em));
    }

    return event;
  });
  return transientEvents;
}

export function getUserAbsences(userId, absences) {
  if (!userId || !absences || !absences.length) {
    return [];
  }
  return absences.filter((absence) => absence.acti_user == userId);
}

export function prepareAbsencePayload(values) {
  const { acti_start_time, acti_end_time, ...rest } = values;
  return {
    ...rest,
    date_from: values.date_from ? toUtcDate(values.date_from) : null,
    date_to: values.date_to ? toUtcDate(values.date_to) : null,
    acti_start_time: acti_start_time,
    acti_end_time: acti_end_time,
  };
}

export function getAbsencesIdsForRange(date_from, date_to, absences) {
  const range = daysRange(date_from, date_to).map((day) =>
    day.format('YYYY-MM-DD'),
  );
  const absences_ids = absences
    .map((a) => range.includes(a.acti_date) && a.id)
    .filter(Boolean);
  return absences_ids;
}

export function getEventTimeDelta(info) {
  if (info.milliseconds !== 0) {
    return info.milliseconds / 1000 / 60;
  }
  return 0;
}

export function shiftTime(time, deltaMinutes) {
  const [h, m] = time.split(':').map(Number);
  const d = dayjs().hour(h).minute(m).second(0).millisecond(0);
  return d.add(deltaMinutes, 'minute').format('HH:mm');
}
